import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import "./style.css";
import { Card, Form, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBarsFilter,
  faEye,
  faTrashCan,
} from "@fortawesome/pro-solid-svg-icons";
import { Button } from "@components/Button";
import { ColumnDef } from "@tanstack/react-table";
import DataTable from "@components/DataTable";
import SearchField from "@components/Forms/SearchField/SearchField";
import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import {
  useV2DeleteBlueprintMutation,
  V2ListBlueprintsForAccountApiArg,
} from "@generated";
import {
  useListBlueprintsForAccount,
  useListTagsForAccount,
  useListServicesForAccount,
} from "./api";
import { Blueprint } from "./api/useListBlueprintsForAccount";
import BlueprintFilters, {
  BlueprintFilterSelections,
} from "./common/BlueprintFilters";
import { BlueprintPreview } from "@components/PreviewSlideouts";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";

const Show = (): JSX.Element => {
  const navigate = useNavigate();
  const { hasPrivilege } = usePrivilegeCheck();
  const canCreate = hasPrivilege("settings.blueprints", ["create", "manage"]);
  const canManage = hasPrivilege("settings.blueprints", ["manage"]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [filterParams, setFilterParams] = useState<
    V2ListBlueprintsForAccountApiArg["filter"]
  >({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [filterSlideoutOpen, setFilterSlideoutOpen] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const [applyFilters, setApplyFilters] = useState(false);
  const [clearAll, setClearAll] = useState(false);
  const [userSelections, setUserSelections] =
    useState<BlueprintFilterSelections>({
      tags: [],
      service: null,
    });
  const [previewBlueprint, setPreviewBlueprint] = useState<Blueprint | null>(
    null
  );
  const [previewOpen, setPreviewOpen] = useState(false);

  const { blueprints, pageMeta, blueprintsLoading, refetch } =
    useListBlueprintsForAccount({
      filter: filterParams,
      page: { number: pageNumber, size: pageSize },
    });

  const { tags: tagOptions, tagsLoading } = useListTagsForAccount();
  const { services: serviceOptions, servicesLoading } =
    useListServicesForAccount();

  const [deleteBlueprint] = useV2DeleteBlueprintMutation();

  // Filter count
  useEffect(() => {
    let count = 0;
    if (userSelections.tags.length) count++;
    if (userSelections.service) count++;
    setFilterCount(count);
  }, [userSelections]);

  // Apply filters
  useEffect(() => {
    if (applyFilters) {
      const params: V2ListBlueprintsForAccountApiArg["filter"] = {};
      if (filterParams?.name) params.name = filterParams.name;
      if (userSelections.tags.length)
        params.tag_list = userSelections.tags.map((t) => t.value).join(",");
      if (userSelections.service)
        params["service-id"] = userSelections.service.value;

      setFilterParams(params);
      setFilterSlideoutOpen(false);
      setApplyFilters(false);
    }
  }, [applyFilters]);

  // Clear all filters
  useEffect(() => {
    if (clearAll && !filterSlideoutOpen) {
      setUserSelections({ tags: [], service: null });
      setFilterParams({ name: filterParams?.name });
      setClearAll(false);
    }
  }, [filterSlideoutOpen]);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFilterParams((prev) => ({ ...prev, name: searchInput || undefined }));
    setPageNumber(1);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteModal(false);
    try {
      await Promise.all(
        selectedIds.map((id) => deleteBlueprint({ id }).unwrap())
      );
      const count = selectedIds.length;
      setSelectedIds([]);
      setSuccessMessage(`${count} blueprint${count !== 1 ? "s" : ""} deleted.`);
      setShowSuccessAlert(true);
      refetch();
    } catch (err: any) {
      setErrorMessage(
        err?.data?.errors?.[0]?.detail || "Failed to delete blueprint(s)."
      );
      setShowFailAlert(true);
    }
  };

  const isEmpty = blueprints.length === 0;

  const columns: ColumnDef<Blueprint>[] = [
    {
      header: "Name",
      cell(props) {
        const isNoRow = props.row.original.id === "noRow";
        return (
          <div
            className={isNoRow ? "" : "blueprintName"}
            onClick={
              isNoRow
                ? undefined
                : () =>
                    navigate(`/admin/blueprints/${props.row.original.id}/edit`)
            }
          >
            {props.row.original.name}
          </div>
        );
      },
    },
    {
      header: "Services in Blueprint",
      cell(props) {
        const bp = props.row.original;
        const services = bp.services ?? [];
        if (bp.id === "noRow" || !services.length) return <div />;
        const preview = services
          .slice(0, 2)
          .map((s) => s.name ?? "")
          .join(", ");
        return (
          <div className="servicesInBlueprint">
            {preview}
            <FontAwesomeIcon
              icon={faEye}
              className="previewBlueprintIcon"
              onClick={() => {
                setPreviewBlueprint(bp);
                setPreviewOpen(true);
              }}
            />
          </div>
        );
      },
    },
    {
      header: "Tags",
      cell(props) {
        const tags = props.row.original.tags ?? [];
        if (!tags.length) return <div />;
        return (
          <div className="tagsContainer">
            {tags.map((tag, i) => (
              <div className="blueprintTag" key={i}>
                {tag}
              </div>
            ))}
          </div>
        );
      },
    },
  ];

  if (blueprintsLoading || tagsLoading || servicesLoading) {
    return <ScopeStackSpinner />;
  }

  return (
    <div className="blueprintsV2">
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      {handleAlert(
        showFailAlert,
        errorMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      <Card>
        <Card.Header style={{ backgroundColor: "#fff" }}>
          <div className="headerContainer">
            <h2>Blueprints</h2>
            {canCreate && (
              <Button onClick={() => navigate("/admin/blueprints/new")}>
                Create Blueprint
              </Button>
            )}
          </div>
          <Row className="searchAndFilter">
            {selectedIds.length > 0 && (
              <div className="selectedBlueprints">
                <span>{selectedIds.length} Selected</span>
                <span className="selectedDivider" />
                <FontAwesomeIcon
                  icon={faTrashCan}
                  className="trashIcon"
                  onClick={() => setShowDeleteModal(true)}
                />
              </div>
            )}
            <div className="search">
              <FormFieldLabel label="Search Blueprints" className={undefined} />
              <Form
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch(e as any);
                }}
                onSubmit={handleSearch}
              >
                <SearchField
                  placeholder="Enter blueprint name"
                  onChange={(e) => setSearchInput(e.target.value)}
                  value={searchInput}
                  onClick={() => {
                    setSearchInput("");
                    setFilterParams((prev) => ({ ...prev, name: undefined }));
                  }}
                  fullWidth
                />
              </Form>
            </div>
            <div className="filter" onClick={() => setFilterSlideoutOpen(true)}>
              <FontAwesomeIcon
                icon={faBarsFilter}
                className={filterCount ? "activeFilters" : ""}
              />
              {filterCount > 0 && (
                <span className="activeFilters">{filterCount} Active</span>
              )}
            </div>
          </Row>
        </Card.Header>
        <DataTable
          data={
            isEmpty
              ? [{ id: "noRow", name: "No blueprints found" } as Blueprint]
              : blueprints
          }
          columns={columns}
          selectable={!isEmpty && canManage}
          bordered
          striped
          hover
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={setPageNumber}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          paginationEnabled
          pageSizeEnabled
        />
      </Card>

      <ScopeStackModal
        show={showDeleteModal}
        modalTitle={`Delete Blueprint${selectedIds.length !== 1 ? "s" : ""}`}
        modalBody={
          <p>
            Are you sure you want to delete {selectedIds.length} blueprint
            {selectedIds.length !== 1 ? "s" : ""}?
          </p>
        }
        button1Text="Cancel"
        handleButton1Click={() => setShowDeleteModal(false)}
        button2Text="Delete"
        handleButton2Click={handleConfirmDelete}
        handleClose={() => setShowDeleteModal(false)}
      />

      <BlueprintFilters
        userSelections={userSelections}
        setUserSelections={setUserSelections}
        slideoutOpen={filterSlideoutOpen}
        setSlideoutOpen={setFilterSlideoutOpen}
        filterCount={filterCount}
        onSave={() => setApplyFilters(true)}
        clearAll={clearAll}
        setClearAll={setClearAll}
        tagOptions={tagOptions}
        serviceOptions={serviceOptions}
      />
      {previewBlueprint && (
        <BlueprintPreview
          blueprint={previewBlueprint}
          slideoutOpen={previewOpen}
          setSlideoutOpen={setPreviewOpen}
          title="Blueprint Preview"
        />
      )}
    </div>
  );
};

export default Show;
